import {
  AppliedFilters,
  Container,
  Divider,
  Heading,
  Table,
  Tour,
  baseStep,
  useConfirmDialog,
  useModal,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { GitRootAddFileButton } from "./add-git-roots-file-button";
import { AddRootButton } from "./add-root-button";
import { Environments } from "./environments";
import { ExportButton } from "./export-button";
import { useAddGitRoot } from "./hooks/add-git-root";
import { useModifyGitRoot } from "./hooks/modify-git-root";
import { useUpdateGitRoot } from "./hooks/update-git-root";
import { useDockerImagesQuery } from "./hooks/use-docker-images-query";
import { useEventsQuery } from "./hooks/use-event-query";
import { useGitRootsDataQuery } from "./hooks/use-git-root-data-query";
import { useGitRootDetailsQuery } from "./hooks/use-git-root-details-query";
import { useGitRootFilterOptionsQuery } from "./hooks/use-git-root-filter-options-query";
import { useGitRootQueries } from "./hooks/use-git-root-queries";
import { ManagementModal } from "./management-modal";
import { OauthRootModal } from "./oauth-root-modal";
import { renderDescriptionComponent } from "./root-description";
import {
  formatRootsData,
  fromDockerImagesToEnvironmentUrl,
  getColumns,
  getInitialValues,
  useGitSubmit,
} from "./utils";

import { DeactivateRootModal } from "../deactivate-root-modal";
import { InternalSurfaceButton } from "../internal-surface-button";
import type {
  IEnvironmentUrlAttr,
  IEnvironmentUrlData,
  IGitRootData,
  IGitRootEventsData,
  IGitRootManagementDetailsAttr,
  TProvider,
} from "../types";
import { Can } from "context/authz/can";
import { useGitRootsFilters } from "features/git-roots/filters";
import type { GitRootsFiltersInput, GitRootsSortInput } from "gql/graphql";
import { useAuthz, useDebouncedCallback, useTour } from "hooks";
import { useObserverAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";
import { Logger } from "utils/logger";
import { msgSuccess } from "utils/notifications";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

interface IGitRootsProps {
  readonly groupName: string;
  readonly organizationName: string;
}

const TIMEOUT_DURATION = 1500;
const DEBOUNCE_DELAY = 800;

export const GitRoots: FC<IGitRootsProps> = ({
  groupName,
  organizationName,
}: IGitRootsProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tableHelpers = useTableServerSideStore();
  const { canMutate, canResolve } = useAuthz();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { ref: rootsRef } = useObserverAudit("Group.Roots", groupName);
  const [modalMessage, setModalMessage] = useState({
    message: "",
    type: "success",
  });
  const canSyncGitRoot = canMutate("sync_git_root");
  const canUpdateRootState = canMutate("activate_root");
  const canShowModal =
    canResolve("git_root_secrets") || canMutate("update_git_root");

  // State management
  const [isManagingRoot, setIsManagingRoot] = useState<
    false | { mode: "ADD" | "EDIT" }
  >(false);
  const { tours, setCompleted } = useTour();
  const enableTour = !tours.newRoot;
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<GitRootsFiltersInput>({});
  const [oauthProvider, setOauthProvider] = useState<TProvider>("");
  const [currentRootId, setCurrentRootId] = useState("");
  const [currentRow, setCurrentRow] = useState<
    IGitRootManagementDetailsAttr | undefined
  >(undefined);
  const addOauthRootModal = useModal("add-oauth-root-modal");
  const deactivateRootModal = useModal("deactivation-root-modal");

  const closeDeactivationModal = useCallback((): void => {
    deactivateRootModal.close();
    setCurrentRootId("");
  }, [deactivateRootModal]);
  const openAddModal = useCallback((): void => {
    if (enableTour) {
      setCompleted("newRoot");
    }
    setIsManagingRoot({ mode: "ADD" });
  }, [enableTour, setCompleted]);
  const openOauthRootModal = useCallback(
    (provider: TProvider): void => {
      setOauthProvider(provider);
      addOauthRootModal.open();
    },
    [addOauthRootModal],
  );
  const closeModal = useCallback((): void => {
    if (enableTour) {
      setCompleted("newRoot");
    }
    setIsManagingRoot(false);
    setModalMessage({ message: "", type: "success" });
  }, [enableTour, setCompleted, setModalMessage]);

  // GraphQL operations
  const { gitRoots, handleNextPage, loadingRoots, rootsRefetch, total } =
    useGitRootsDataQuery(
      groupName,
      filters,
      tableHelpers.pagination.pageSize *
        (tableHelpers.pagination.pageIndex + 1),
      search,
      tableHelpers.sortBy as unknown as GitRootsSortInput[],
    );
  const { gitBranches, gitNickNames } = useGitRootFilterOptionsQuery(groupName);
  const {
    externalId,
    gitEnvironmentUrls,
    loading: loadingGitEnvironmentUrls,
    refetch,
  } = useGitRootQueries(groupName, organizationName);
  const { dockerImagesData } = useDockerImagesQuery(groupName);
  const { eventsData } = useEventsQuery(groupName);
  const { requestRootInfo } = useGitRootDetailsQuery();

  const onUpdate = useCallback((): void => {
    setTimeout((): void => {
      void refetch();
      void rootsRefetch();
    }, TIMEOUT_DURATION);
  }, [refetch, rootsRefetch]);

  const { addGitRoot } = useAddGitRoot(onUpdate, closeModal, setModalMessage);
  const { updateGitRoot } = useUpdateGitRoot(
    onUpdate,
    closeModal,
    setModalMessage,
    setCurrentRow,
  );

  const { activateRoot, checkEvents, loading, syncGitRoot } = useModifyGitRoot(
    onUpdate,
    setCurrentRow,
  );

  // Event handlers
  const handleSyncClick = useCallback(
    (row: IGitRootData): void => {
      void syncGitRoot({ variables: { groupName, rootId: row.id } });
    },
    [groupName, syncGitRoot],
  );

  const handleRowClick = useCallback(
    async (rowInfo: Row<IGitRootData>): Promise<void> => {
      if (rowInfo.original.state === "ACTIVE") {
        const result = await requestRootInfo({
          variables: {
            groupName: groupName.toLocaleLowerCase(),
            rootId: rowInfo.original.id,
          },
        });
        setCurrentRow(result.data?.root);
        setIsManagingRoot({ mode: "EDIT" });
      }
    },
    [groupName, requestRootInfo],
  );

  const handleGitSubmit = useGitSubmit(
    addGitRoot,
    groupName,
    isManagingRoot,
    setModalMessage,
    updateGitRoot,
  );

  useEffect((): void => {
    gitEnvironmentUrls.forEach((envData): void => {
      if (envData.urlType === "CSPM") {
        void checkEvents({
          variables: { groupName, urlId: envData.id },
        });
      }
    });
  }, [gitEnvironmentUrls, checkEvents, groupName]);

  const gitEnvironmentDockerEnvs = useMemo((): IEnvironmentUrlAttr[] => {
    const existingUrls = gitEnvironmentUrls;
    const dockerImages = dockerImagesData?.group.dockerImages ?? [];

    const transformedDockerImages =
      fromDockerImagesToEnvironmentUrl(dockerImages);

    return [...existingUrls, ...transformedDockerImages];
  }, [dockerImagesData, gitEnvironmentUrls]);

  const roots = useMemo(
    (): IGitRootData[] => formatRootsData(gitRoots, gitEnvironmentUrls),
    [gitEnvironmentUrls, gitRoots],
  );
  const handleSearch = useDebouncedCallback((searchValue: string): void => {
    setSearch(searchValue);
  }, DEBOUNCE_DELAY);

  const envUrlsDataSet = useMemo((): IEnvironmentUrlData[] => {
    const envUrlsDataAt = gitEnvironmentDockerEnvs.reduce<
      Record<string, IEnvironmentUrlData>
    >((previousValue, currentValue): Record<string, IEnvironmentUrlData> => {
      const { url, include, root } = currentValue;

      if (root.state === "INACTIVE") return previousValue;

      return {
        ...previousValue,
        [url]: {
          ...currentValue,
          repositoryUrls: [
            ...(url in previousValue ? previousValue[url].repositoryUrls : []),
            { include, root },
          ],
        },
      };
    }, {});

    return Object.values(envUrlsDataAt);
  }, [gitEnvironmentDockerEnvs]);

  const combinedData = useMemo(
    (): IGitRootEventsData[] =>
      roots.map((rootData): IGitRootEventsData => {
        const matchedEvent = eventsData.find(
          (event): boolean =>
            event.root?.id === rootData.id &&
            event.eventStatus === "Unsolved" &&
            event.environment === null,
        );

        return {
          ...rootData,
          cloningStatus: {
            ...rootData.cloningStatus,
            status:
              rootData.state === "ACTIVE"
                ? rootData.cloningStatus.status
                : "INACTIVE",
          },
          eventId: matchedEvent?.id ?? undefined,
          eventStatus: matchedEvent?.eventStatus ?? undefined,
          status: rootData.cloningStatus.status,
        };
      }),
    [roots, eventsData],
  );

  const handleRowExpand = useCallback(
    (row: Row<IGitRootData>): JSX.Element => {
      return renderDescriptionComponent(row.original, groupName);
    },
    [groupName],
  );

  const handleStateUpdate = useCallback(
    (row: Record<string, string>): void => {
      if (row.state === "ACTIVE") {
        deactivateRootModal.open();
        setCurrentRootId(row.id);
      } else {
        confirm({
          title: t("group.scope.common.confirm"),
        })
          .then((confirmResult: boolean): void => {
            if (confirmResult) {
              void activateRoot({ variables: { groupName, id: row.id } });
              msgSuccess(t("group.scope.common.success"));
            }
          })
          .catch((): void => {
            Logger.error("An error occurred confirming state");
          });
      }
    },
    [activateRoot, confirm, deactivateRootModal, groupName, t],
  );

  const gitRootsColumns = getColumns(
    canUpdateRootState,
    canSyncGitRoot,
    handleStateUpdate,
    handleSyncClick,
    loading,
  );

  const { Filters, options, removeFilter } = useGitRootsFilters({
    gitBranches,
    gitNickNames,
    groupName,
    loading: loadingRoots,
    setFilters,
  });

  const { table } = useTable({
    columns: gitRootsColumns,
    data: combinedData,
    fetchMore: handleNextPage,
    filters: tableHelpers as unknown as TStore<IGitRootEventsData, string>,
    id: "git-roots-table",
    options: {
      actionsOptions: { onSearch: handleSearch, search },
      columnOptions: {
        hidden: ["hck"],
        order: [
          "url",
          "branch",
          "status",
          "priority",
          "cloning_status",
          "nickname",
          "sync",
        ],
        pinned: ["url"],
      },
      rowOptions: {
        expandRender: handleRowExpand,
        getId: (row): string => `${row.id}-${row.branch}`,
        onClick: canShowModal
          ? (handleRowClick as (row: Row<IGitRootData>) => void)
          : undefined,
      },
    },
    total,
  });

  return (
    <Fragment>
      <Container ref={rootsRef}>
        <Heading fontWeight={"bold"} mb={1.25} size={"sm"}>
          {t("group.scope.git.title")}
        </Heading>
        <Table table={table}>
          <Table.Options>
            <Table.Controls>
              <Filters />
              <Table.Columns table={table} />
              <Container gap={0.5} id={"actions-container"}>
                <ExportButton />
                <Can do={"integrates_api_mutations_add_git_root_mutate"}>
                  <Can
                    do={"integrates_api_mutations_upload_git_root_file_mutate"}
                  >
                    <GitRootAddFileButton />
                  </Can>
                </Can>
                <Can do={"integrates_api_mutations_add_git_root_mutate"}>
                  <AddRootButton
                    manualClick={openAddModal}
                    providersClick={openOauthRootModal}
                  />
                  {enableTour ? (
                    <Tour
                      run={false}
                      steps={[
                        {
                          ...baseStep,
                          content: t("tours.addGitRoot.addButton"),
                          disableBeacon: true,
                          hideFooter: true,
                          target: "#git-root-add",
                        },
                      ]}
                    />
                  ) : undefined}
                </Can>
                <InternalSurfaceButton />
              </Container>
            </Table.Controls>
          </Table.Options>
          <Table.Caption>
            <AppliedFilters onClose={removeFilter} options={options} />
          </Table.Caption>
          <Table.Content />
          <Table.Footer />
        </Table>
      </Container>
      <ConfirmDialog />
      {loadingGitEnvironmentUrls || envUrlsDataSet.length === 0 ? undefined : (
        <Fragment>
          <Divider color={theme.palette.gray[200]} mb={1.25} mt={1.25} />
          <Environments
            envUrlsDataSet={envUrlsDataSet}
            eventsDataSet={eventsData}
            groupName={groupName}
          />
        </Fragment>
      )}
      {isManagingRoot === false || externalId === undefined ? undefined : (
        <ManagementModal
          externalId={externalId}
          groupName={groupName}
          initialValues={
            isManagingRoot.mode === "EDIT"
              ? getInitialValues(currentRow)
              : undefined
          }
          isEditing={isManagingRoot.mode === "EDIT"}
          manyRows={false}
          modalMessages={modalMessage}
          onClose={closeModal}
          onSubmitRepo={handleGitSubmit}
          onUpdate={onUpdate}
          organizationName={organizationName}
        />
      )}
      <DeactivateRootModal
        groupName={groupName}
        modalRef={{ ...deactivateRootModal, close: closeDeactivationModal }}
        onUpdate={onUpdate}
        rootId={currentRootId}
      />
      <OauthRootModal
        addOauthRootModal={addOauthRootModal}
        oauthProvider={oauthProvider}
        onUpdate={onUpdate}
      />
    </Fragment>
  );
};
